import { useRef, useState, type DragEvent } from "react";
import { Link } from "react-router";
import { useNotify } from "ra-core";
import { FileUp, Paperclip, Trash2 } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

import { BUCKET, belegOeffnen, pruefe, sb } from "./daten";
import { belegeAnlegen, usePostfachAbgleich } from "./belegAnlegen";
import {
  buchungsWerte,
  steuerAusBeleg,
  vorbelegung,
  vorschlaege,
} from "./kern/beleg";
import { naechsteTermine, taktAm, zeitraeume } from "./kern/fristen";
import {
  artVon,
  euerRechnen,
  ustvaMonat,
  ustvaQuartal,
  type Posten,
} from "./kern/rechnen";
import { KATEGORIEN, cent, type Beleg, type Buchung } from "./kern/typen";
import { KontoSpalte } from "./KontoSpalte";
import {
  Auswahl,
  Feld,
  Hinweis,
  Tabelle,
  Zahl,
  fmtCent,
  fmtDatum,
  heute,
  zahlLesen,
  type TabProps,
} from "./teile";

const EINTRAEGE = 8;

const useAktion = (neuLaden: TabProps["neuLaden"]) => {
  const notify = useNotify();
  return async (aktion: () => Promise<unknown>) => {
    try {
      await aktion();
      await neuLaden();
      return true;
    } catch (e) {
      notify(e instanceof Error ? e.message : String(e), { type: "error" });
      return false;
    }
  };
};

export const EinnahmenUeberschuss = (
  props: TabProps & { wechsel: (b: string) => void },
) => {
  const { basis, daten, posten, punkte, wechsel } = props;
  const notify = useNotify();
  usePostfachAbgleich(daten.belege, props.neuLaden, (n) =>
    notify(`${n} ${n === 1 ? "Rechnung" : "Rechnungen"} aus den Postfächern bei den Ausgaben abgelegt`),
  );
  const { jahr } = basis;
  const euer = euerRechnen(posten, jahr);
  const imJahr = posten.filter((p) => p.datum?.startsWith(`${jahr.jahr}-`));
  const einnahmen = imJahr.filter((p) => artVon(p.kategorie) === "Einnahme");
  const ausgaben = imJahr.filter((p) =>
    ["Ausgabe", "Finanzamt", "Pauschale"].includes(artVon(p.kategorie)),
  );
  const summe = (l: Posten[]) => l.reduce((s, p) => s + p.brutto, 0);
  const wert = (position: string) =>
    euer.zeilen.find((z) => z.position === position)?.betrag ?? 0;
  const finanzamt =
    wert("Vereinnahmte USt") +
    wert("Gezahlte Vorsteuer") +
    wert("Ans Finanzamt gezahlte USt (Erstattung positiv)");
  const offen = posten.filter(
    (p) => !p.kategorie || p.unsicher.length || p.duplikat,
  ).length;

  return (
    <div className="space-y-4">
      <div className="rounded-lg border bg-card p-4">
        <div className="text-xs text-muted-foreground">Saldo {jahr.jahr}</div>
        <div className="text-3xl font-semibold tabular-nums">
          {fmtCent(euer.gewinn)}
        </div>
        <div className="text-sm text-muted-foreground">
          {finanzamt > 0
            ? `davon ${fmtCent(finanzamt)} Umsatzsteuer, die ans Finanzamt geht`
            : finanzamt < 0
              ? `dazu ${fmtCent(-finanzamt)} Vorsteuer, die vom Finanzamt kommt`
              : "keine offene Umsatzsteuer"}
        </div>
      </div>

      <div className="grid items-start gap-4 md:grid-cols-2 xl:grid-cols-3">
        <Spalte
          {...props}
          richtung="einnahme"
          titel="Einnahmen"
          liste={einnahmen}
          gesamt={summe(einnahmen)}
        />
        <Spalte
          {...props}
          richtung="ausgabe"
          titel="Ausgaben"
          liste={ausgaben}
          gesamt={summe(ausgaben)}
        />
        <KontoSpalte {...props} />
      </div>

      <Voranmeldungen {...props} />

      {(offen > 0 || punkte.length > 0) && (
        <p className="text-sm text-muted-foreground">
          {offen > 0 && (
            <Button
              variant="link"
              className="h-auto p-0"
              onClick={() => wechsel("rueckfragen")}
            >
              {offen} Buchungen warten auf eine Entscheidung
            </Button>
          )}
          {offen > 0 && " · "}
          <Button
            variant="link"
            className="h-auto p-0"
            onClick={() => wechsel("auswertung")}
          >
            {punkte.length} Prüfpunkte
          </Button>
          {daten.rechnungen.some((r) => r.status === "offen") &&
            ` · ${daten.rechnungen.filter((r) => r.status === "offen").length} offene Rechnungen`}
        </p>
      )}
    </div>
  );
};

type SpalteProps = TabProps & {
  richtung: Beleg["richtung"];
  titel: string;
  liste: Posten[];
  gesamt: number;
};

const Spalte = ({ richtung, titel, liste, gesamt, ...props }: SpalteProps) => {
  const { daten, neuLaden } = props;
  const [ueber, setUeber] = useState(false);
  const [alle, setAlle] = useState(false);
  const [laeuft, setLaeuft] = useState(false);
  const eingabe = useRef<HTMLInputElement>(null);
  const aktion = useAktion(neuLaden);
  const offene = daten.belege.filter(
    (b) => b.richtung === richtung && !b.buchung_id && !b.rechnung_id && !b.verworfen,
  );
  const belegZu = new Map(daten.belege.map((b) => [b.buchung_id, b]));
  const sortiert = [...liste].reverse();
  const sichtbar = alle ? sortiert : sortiert.slice(0, EINTRAEGE);

  const hochladen = async (dateien: FileList | File[]) => {
    setLaeuft(true);
    await aktion(() => belegeAnlegen(Array.from(dateien), richtung));
    setLaeuft(false);
  };

  const ablegen = (e: DragEvent) => {
    e.preventDefault();
    setUeber(false);
    if (e.dataTransfer.files.length) hochladen(e.dataTransfer.files);
  };

  return (
    <section
      aria-label={titel}
      className={cn(
        "space-y-3 rounded-lg border bg-card p-4",
        ueber && "ring-2 ring-primary",
      )}
      onDragOver={(e) => {
        e.preventDefault();
        setUeber(true);
      }}
      onDragLeave={() => setUeber(false)}
      onDrop={ablegen}
    >
      <div className="flex items-baseline justify-between gap-2">
        <h2 className="font-semibold">{titel}</h2>
        <span className="text-lg font-semibold tabular-nums">
          {fmtCent(Math.abs(gesamt))}
        </span>
      </div>

      <button
        type="button"
        onClick={() => eingabe.current?.click()}
        disabled={laeuft}
        className="flex w-full cursor-pointer items-center justify-center gap-2 rounded-md border border-dashed py-3 text-sm text-muted-foreground hover:bg-muted"
      >
        <FileUp className="h-4 w-4" />
        {laeuft ? "Lädt hoch …" : "Beleg ablegen oder auswählen"}
      </button>
      <input
        ref={eingabe}
        type="file"
        multiple
        hidden
        aria-label={`Beleg ${titel}`}
        accept=".pdf,image/*"
        onChange={(e) => {
          if (e.target.files?.length) hochladen(e.target.files);
          e.target.value = "";
        }}
      />

      {offene.map((b) => (
        <BelegKarte key={b.id} beleg={b} {...props} />
      ))}

      {sichtbar.length === 0 && !offene.length && (
        <p className="text-sm text-muted-foreground">Noch nichts gebucht.</p>
      )}
      <ul className="divide-y text-sm">
        {sichtbar.map((p) => {
          const beleg = p.buchungId ? belegZu.get(p.buchungId) : undefined;
          return (
            <li
              key={p.id}
              className="flex items-center justify-between gap-2 py-1.5"
            >
              <div className="min-w-0">
                <div className="truncate">{p.partner || p.text}</div>
                <div className="text-xs text-muted-foreground">
                  {fmtDatum(p.datum)}
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-1">
                {beleg && (
                  <button
                    type="button"
                    title={beleg.dateiname}
                    aria-label="Beleg ansehen"
                    className="cursor-pointer text-muted-foreground"
                    onClick={() => belegOeffnen(beleg.pfad)}
                  >
                    <Paperclip className="h-4 w-4" />
                  </button>
                )}
                <span className="tabular-nums">
                  {fmtCent(Math.abs(p.brutto))}
                </span>
              </div>
            </li>
          );
        })}
      </ul>
      {sortiert.length > EINTRAEGE && (
        <Button
          variant="link"
          className="h-auto p-0"
          onClick={() => setAlle(!alle)}
        >
          {alle ? "Weniger zeigen" : `Alle ${sortiert.length} zeigen`}
        </Button>
      )}
    </section>
  );
};

const BelegKarte = ({
  beleg,
  basis,
  daten,
  neuLaden,
}: TabProps & { beleg: Beleg }) => {
  const { jahr } = basis;
  const aktion = useAktion(neuLaden);
  const einnahme = beleg.richtung === "einnahme";
  const [datum, setDatum] = useState(beleg.datum ?? heute());
  const [brutto, setBrutto] = useState(
    beleg.brutto ? String(beleg.brutto).replace(".", ",") : "",
  );
  const [partner, setPartner] = useState(beleg.partner);
  const [nummer, setNummer] = useState(beleg.nummer ?? "");
  const [misch, setMisch] = useState("");
  const [mischOffen, setMischOffen] = useState(false);
  const [eigeneWahl, setEigeneWahl] = useState<{
    kategorie?: string;
    satz?: number;
  }>({});

  const entwurf: Beleg = {
    ...beleg,
    datum,
    brutto: zahlLesen(brutto) || null,
    brutto_ermaessigt: zahlLesen(misch) || null,
    partner,
    nummer: nummer || null,
  };
  const liste = vorschlaege(
    entwurf,
    daten.buchungen,
    daten.belege,
    daten.konten,
  );
  const erster = liste[0]?.buchung;
  const vor = vorbelegung(
    entwurf,
    partner || erster?.partner || "",
    daten.regeln,
    jahr,
  );
  const kategorie = eigeneWahl.kategorie ?? vor.kategorie;
  const satz = eigeneWahl.satz ?? vor.satz;
  const bereit = !!entwurf.brutto && !!kategorie;
  const steuer = entwurf.brutto ? steuerAusBeleg(entwurf, satz, jahr) : 0;

  const belegFelder = {
    datum,
    brutto: entwurf.brutto,
    brutto_ermaessigt: entwurf.brutto_ermaessigt,
    partner,
    nummer: nummer || null,
    kategorie,
    satz,
  };

  const zuordnen = (b: Buchung) =>
    aktion(async () => {
      const { betrag: _, ...werte } = buchungsWerte(
        entwurf,
        kategorie,
        satz,
        jahr,
      );
      pruefe(await sb().from("bh_buchung").update(werte).eq("id", b.id));
      pruefe(
        await sb()
          .from("bh_beleg")
          .update({ ...belegFelder, buchung_id: b.id })
          .eq("id", beleg.id),
      );
    });

  const ohneKonto = (quelle: "bar" | "einlage") =>
    aktion(async () => {
      const { data } = pruefe(
        await sb()
          .from("bh_buchung")
          .insert({
            ...buchungsWerte(entwurf, kategorie, satz, jahr),
            quelle,
            datum,
            partner,
            text: nummer,
          })
          .select("id")
          .single(),
      );
      pruefe(
        await sb()
          .from("bh_beleg")
          .update({ ...belegFelder, buchung_id: data?.id })
          .eq("id", beleg.id),
      );
    });

  const offeneRechnung = () =>
    aktion(async () => {
      const voll = cent(entwurf.brutto ?? 0);
      const teil = cent(entwurf.brutto_ermaessigt ?? 0);
      const sieben = teil > 0 ? teil : satz === jahr.satz_ermaessigt ? voll : 0;
      const null_ = satz === 0 && !teil ? voll : 0;
      const { data } = pruefe(
        await sb()
          .from("bh_rechnung")
          .insert({
            nummer: nummer || beleg.dateiname,
            kunde: partner,
            rechnungsdatum: datum,
            brutto_19: (voll - sieben - null_) / 100,
            brutto_7: sieben / 100,
            brutto_0: null_ / 100,
          })
          .select("id")
          .single(),
      );
      pruefe(
        await sb()
          .from("bh_beleg")
          .update({ ...belegFelder, rechnung_id: data?.id })
          .eq("id", beleg.id),
      );
    });

  const entfernen = () =>
    aktion(async () => {
      const { error } = await sb()
        .storage.from(BUCKET)
        .remove([beleg.pfad, ...(beleg.pfad_xml ? [beleg.pfad_xml] : [])]);
      if (error) throw new Error(error.message);
      // Belege aus dem Postfach bleiben als verworfen stehen, sonst legt der Abgleich sie neu an
      pruefe(
        beleg.email_id
          ? await sb().from("bh_beleg").update({ verworfen: true }).eq("id", beleg.id)
          : await sb().from("bh_beleg").delete().eq("id", beleg.id),
      );
    });

  return (
    <div className="space-y-2 rounded-md border bg-muted/40 p-3">
      <div className="flex items-center justify-between gap-2 text-sm">
        <button
          type="button"
          className="min-w-0 cursor-pointer truncate underline"
          onClick={() => belegOeffnen(beleg.pfad)}
        >
          {beleg.dateiname}
        </button>
        <Button
          variant="ghost"
          size="icon"
          aria-label="Beleg entfernen"
          onClick={entfernen}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
      {(beleg.hinweis || beleg.email_id || beleg.firma_id) && (
        <p className="text-xs text-muted-foreground">
          {beleg.email_id ? "Aus dem Postfach. " : ""}
          {beleg.hinweis}
          {beleg.firma_id && (
            <>
              {" "}
              <Link to={`/companies/${beleg.firma_id}/show`} className="underline">
                Firma im CRM
              </Link>
            </>
          )}
        </p>
      )}
      <div className="grid grid-cols-2 gap-2">
        <Feld name="Brutto">
          <Input
            inputMode="decimal"
            aria-label="Brutto"
            value={brutto}
            onChange={(e) => setBrutto(e.target.value)}
          />
        </Feld>
        <Feld name="Datum">
          <Input
            type="date"
            aria-label="Belegdatum"
            value={datum}
            onChange={(e) => setDatum(e.target.value)}
          />
        </Feld>
        <Feld name={einnahme ? "Kunde" : "Lieferant"}>
          <Input
            aria-label="Partner"
            value={partner}
            onChange={(e) => setPartner(e.target.value)}
          />
        </Feld>
        <Feld name="Belegnummer">
          <Input
            aria-label="Belegnummer"
            value={nummer}
            onChange={(e) => setNummer(e.target.value)}
          />
        </Feld>
        {!einnahme && (
          <Feld name="Kategorie">
            <Auswahl
              aria-label="Belegkategorie"
              value={kategorie}
              onChange={(e) =>
                setEigeneWahl({ ...eigeneWahl, kategorie: e.target.value })
              }
            >
              <option value="">bitte wählen</option>
              {KATEGORIEN.map((k) => (
                <option key={k} value={k}>
                  {k}
                </option>
              ))}
            </Auswahl>
          </Feld>
        )}
        <Feld name="Satz">
          <Auswahl
            aria-label="Belegsatz"
            value={satz}
            disabled={!!entwurf.brutto_ermaessigt}
            onChange={(e) =>
              setEigeneWahl({ ...eigeneWahl, satz: Number(e.target.value) })
            }
          >
            <option value={jahr.satz_regel}>{jahr.satz_regel} %</option>
            <option value={jahr.satz_ermaessigt}>
              {jahr.satz_ermaessigt} %
            </option>
            <option value={0}>0 %</option>
          </Auswahl>
        </Feld>
        {mischOffen ? (

        <Feld
          name={`Davon zu ${jahr.satz_ermaessigt} % (nur bei Mischbeleg)`}
          className="col-span-2"
        >
          <Input
            inputMode="decimal"
            aria-label="Mischanteil"
            value={misch}
            onChange={(e) => setMisch(e.target.value)}
          />
        </Feld>
        ) : (
          <Button variant="link" className="col-span-2 h-auto justify-start p-0" onClick={() => setMischOffen(true)}>
            Mischbeleg {jahr.satz_ermaessigt}/{jahr.satz_regel} %
          </Button>
        )}
      </div>
      {!!entwurf.brutto && (
        <p className="text-sm text-muted-foreground">
          {einnahme ? "USt" : "Vorsteuer"}:{" "}
          <span className="tabular-nums">{fmtCent(steuer)}</span>
        </p>
      )}
      {liste.length > 0 && (
        <div className="space-y-1">
          <p className="text-xs text-muted-foreground">Passende Kontobuchung</p>
          {liste.slice(0, 3).map(({ buchung: b }) => (
            <Button
              key={b.id}
              variant="outline"
              size="sm"
              className="h-auto w-full justify-between py-1.5 whitespace-normal"
              disabled={!bereit}
              onClick={() => zuordnen(b)}
            >
              <span className="text-left">
                {fmtDatum(b.datum)} · {b.partner || b.text}
              </span>
              <Badge variant="secondary">zuordnen</Badge>
            </Button>
          ))}
        </div>
      )}
      {!!entwurf.brutto && liste.length === 0 && (
        <p className="text-xs text-muted-foreground">
          Keine passende Kontobuchung gefunden.
        </p>
      )}
      <div className="flex flex-wrap gap-1">
        {einnahme ? (
          <>
            <Button
              size="sm"
              variant="ghost"
              disabled={!bereit}
              onClick={offeneRechnung}
            >
              Noch nicht bezahlt
            </Button>
            <Button
              size="sm"
              variant="ghost"
              disabled={!bereit}
              onClick={() => ohneKonto("bar")}
            >
              Bar erhalten
            </Button>
          </>
        ) : (
          <>
            <Button
              size="sm"
              variant="ghost"
              disabled={!bereit}
              onClick={() => ohneKonto("einlage")}
            >
              Privat bezahlt
            </Button>
            <Button
              size="sm"
              variant="ghost"
              disabled={!bereit}
              onClick={() => ohneKonto("bar")}
            >
              Bar bezahlt
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

const Voranmeldungen = ({
  basis,
  daten,
  posten,
  wechsel,
}: TabProps & { wechsel: (b: string) => void }) => {
  const { jahr } = basis;
  const tagHeute = heute();
  const taktJetzt = taktAm(
    daten.takte,
    tagHeute.startsWith(`${jahr.jahr}-`) ? tagHeute : `${jahr.jahr}-12-31`,
  );
  const termine = naechsteTermine(zeitraeume(jahr, daten.takte), tagHeute, 4);
  return (
    <section className="space-y-2 rounded-lg border bg-card p-4">
      <div className="flex items-baseline justify-between gap-2">
        <h2 className="font-semibold">Voranmeldungen</h2>
        <Button variant="link" className="h-auto p-0" onClick={() => wechsel("auswertung")}>Alle Zeiträume</Button>
      </div>
      {taktJetzt === "offen" && (
        <Hinweis art="warnung">
          UStVA-Takt offen, Monate und Quartale stehen gleichwertig.{" "}
          <Button
            variant="link"
            className="h-auto p-0"
            onClick={() => wechsel("setup")}
          >
            Im Setup eintragen
          </Button>
        </Hinweis>
      )}
      {!jahr.kur_verzicht && (
        <Hinweis art="warnung">
          Kleinunternehmerregelung aktiv: keine USt, keine Vorsteuer.
        </Hinweis>
      )}
      {termine.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {taktJetzt === "befreit"
            ? "Befreit von der Voranmeldung."
            : `Keine Termine mehr für ${jahr.jahr}.`}
        </p>
      ) : (
        <Tabelle
          kopf={["Zeitraum", "USt", "Vorsteuer", "Zahllast", "Frist"]}
          rechts={[1, 2, 3]}
        >
          {termine.map((z) => {
            const w =
              z.art === "monat"
                ? ustvaMonat(posten, jahr, z.nummer)
                : ustvaQuartal(posten, jahr, z.nummer);
            return (
              <tr key={z.id}>
                <td className="whitespace-nowrap">
                  {z.name}{" "}
                  {!w.bewegung && (
                    <Badge variant="secondary">Nullmeldung</Badge>
                  )}
                </td>
                <Zahl>{fmtCent(w.ust81 + w.ust86)}</Zahl>
                <Zahl>{fmtCent(w.kz66)}</Zahl>
                <Zahl>{fmtCent(w.zahllast)}</Zahl>
                <td className="whitespace-nowrap">{fmtDatum(z.frist)}</td>
              </tr>
            );
          })}
        </Tabelle>
      )}
    </section>
  );
};
